import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { axiosInstance } from '../../utils';
import { useAppContext } from '../context';

export type AiModelDetail = {
  modelName: string;
  creditsPerUnit: number;
  unitType: string;
  generationType: string;
  generationLimit: number;
  alphaGenerationLimit: number;
};

const DEFAULT_ON_LIMIT = 20;

export const useAiModelManagement = () => {
  const { userData, isUserDataLoading } = useAppContext();
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  const [models, setModels] = useState<AiModelDetail[]>([]);
  const [draftLimits, setDraftLimits] = useState<Record<string, Record<string, number>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [savingModel, setSavingModel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = !!userData?.isAdmin;

  const loadModels = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/api/users/admin/ai-modelsdetail');
      const payload: AiModelDetail[] = res?.data || [];
      setModels(payload);
      const draft: Record<string, Record<string, number>> = {};
      payload.forEach((m) => {
        draft[m.modelName] = {
          generationLimit: m.generationLimit ?? 0,
          alphaGenerationLimit: m.alphaGenerationLimit ?? 0,
        };
      });
      setDraftLimits(draft);
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Failed to load AI models';
      setError(message);
      enqueueSnackbar?.(message, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      loadModels();
    }
  }, [isAdmin, loadModels]);

  useEffect(() => {
    if (!isUserDataLoading && userData && !isAdmin) {
      router.replace('/dashboard');
    }
  }, [isAdmin, isUserDataLoading, router, userData]);

  const setDraftLimit = useCallback(
    (modelName: string, value: { generationLimit: number; alphaGenerationLimit: number }) => {
      setDraftLimits((prev) => ({ ...prev, [modelName]: value }));
    },
    []
  );

  const updateLimit = useCallback(
    async (
      modelName: string,
      limit: number,
      key: 'generationLimit' | 'alphaGenerationLimit' = 'generationLimit'
    ) => {
      setSavingModel(modelName);
      try {
        const res = await axiosInstance.patch('/api/users/admin/ai-modelgenerationlimit', {
          modelName,
          generationLimit: limit,
          key,
        });
        const updatedLimit = {
          generationLimit: res?.data?.generationLimit ?? limit,
          alphaGenerationLimit: res?.data?.alphaGenerationLimit ?? limit,
        };
        setModels((prev) =>
          prev.map((m) =>
            m.modelName === modelName
              ? {
                  ...m,
                  generationLimit: updatedLimit.generationLimit,
                  alphaGenerationLimit: updatedLimit.alphaGenerationLimit,
                }
              : m
          )
        );
        setDraftLimits((prev) => ({ ...prev, [modelName]: updatedLimit }));
        enqueueSnackbar?.('Generation limit updated', { variant: 'success' });
      } catch (e: any) {
        const message = e?.response?.data?.message || 'Failed to update generation limit';
        enqueueSnackbar?.(message, { variant: 'error' });
        // throw e;
      } finally {
        setSavingModel(null);
      }
    },
    [enqueueSnackbar]
  );

  // const toggleModel = useCallback(
  //   async (modelName: string, enabled: boolean) => {
  //     const nextLimit = enabled
  //       ? draftLimits[modelName] > 0
  //         ? draftLimits[modelName]
  //         : DEFAULT_ON_LIMIT
  //       : 0;
  //     await updateLimit(modelName, nextLimit);
  //   },
  //   [draftLimits, updateLimit]
  // );

  const savingSet = useMemo(() => new Set(savingModel ? [savingModel] : []), [savingModel]);

  return {
    models,
    draftLimits,
    setDraftLimit,
    isLoading,
    isSaving: savingSet,
    error,
    loadModels,
    updateLimit,
    // toggleModel,
    isAdmin,
    isUserDataLoading,
    userData,
  };
};

export default useAiModelManagement;
